
import { useMemo } from "react";
import { INVENTORY_SUB_TABS, RETURNS_SUB_TABS } from "../../../constants/globalConstants";

export const useInventoryFilters = (state, userObj) => {
  //updated Halimunnisa 2026-06-08
  //Refactored the useInventoryFilters hook to include robust data normalization, flexible and comprehensive filtering logic, and enhanced search capabilities. This allows for a more intuitive and powerful inventory management experience, enabling users to easily find and analyze inventory items based on a wide range of criteria, including category, supplier, status, and date ranges. The hook now also gracefully handles various data structures and edge cases, ensuring consistent performance and reliability across the application.
  let {
    inventory, materialRequests, materialReturns, sales, purchaseReturns, purchases,
    openingStocks, suppliers, cats, subCats, damagedItems,
    projectsLocal, masterItems, activeTab, search, filters = {}, returnsSubTab
  } = state || {};

  inventory = Array.isArray(inventory) ? inventory : [];
  materialRequests = Array.isArray(materialRequests) ? materialRequests : [];
  materialReturns = Array.isArray(materialReturns) ? materialReturns : [];
  damagedItems = Array.isArray(damagedItems) ? damagedItems : [];
  sales = Array.isArray(sales) ? sales : [];
  purchaseReturns = Array.isArray(purchaseReturns) ? purchaseReturns : [];
  purchases = Array.isArray(purchases) ? purchases : [];
  openingStocks = Array.isArray(openingStocks) ? openingStocks : [];
  suppliers = Array.isArray(suppliers) ? suppliers : [];
  cats = Array.isArray(cats) ? cats : [];
  //updated Halimunnisa 2026-06-08
  //Added data normalization for subcategories to ensure that the filtering logic can reliably access and match subcategory information, regardless of how it is structured in the data. This enhancement allows for more accurate and consistent filtering of inventory items based on their associated subcategories, improving the overall user experience when managing and analyzing inventory data.
  subCats = Array.isArray(subCats) ? subCats : [];
  projectsLocal = Array.isArray(projectsLocal) ? projectsLocal : [];
  masterItems = Array.isArray(masterItems) ? masterItems : [];

  const distributedMap = useMemo(() => {
    const map = {};
    materialRequests.forEach(req => {
      if (['dispatched', 'delivered', 'partially_delivered', 'fulfilled'].includes(req.status)) {
        req.items?.forEach(it => {
          const itemId = it.item?._id || it.item;
          if (itemId) {
            const idStr = itemId.toString();
            map[idStr] = (map[idStr] || 0) + (it.deliveredQty || it.qty || 0);
          }
        });
      }
    });
    sales.forEach(sale => {
      if (sale.status === 'confirmed' || sale.recordType === 'sale') {
        sale.items?.forEach(it => {
          const itemId = it.item?._id || it.item;
          if (itemId) {
            const idStr = itemId.toString();
            map[idStr] = (map[idStr] || 0) + (it.qty || 0);
          }
        });
      }
    });
    return map;
  }, [materialRequests, sales]);

  const nameToIdMap = useMemo(() => {
    const map = {};
    masterItems.forEach(mi => {
      const name = (mi.itemName || mi.name || '').trim().toLowerCase();
      if (name) map[name] = mi._id.toString();
    });
    return map;
  }, [masterItems]);

  const purchasedMap = useMemo(() => {
    const map = {};
    
    // 1. Accumulate from regular Purchases
    purchases.forEach(p => {
      const items = (p.items && Array.isArray(p.items)) ? p.items : [p];
      items.forEach(it => {
        const itemId = it.item?._id || it.item;
        if (itemId) {
          const idStr = itemId.toString();
          if (!map[idStr]) map[idStr] = { qty: 0, amount: 0, hasPurchase: true };
          const q = Number(it.quantity || it.qty || it.availableQty || 0);
          const r = Number(it.purchaseRate || it.rate || it.unitPrice || it.price || 0);
          map[idStr].qty += q;
          map[idStr].amount += (q * r);
          map[idStr].hasPurchase = true;
        }
      });
    });

    // 2. Accumulate from Opening Stocks (matching by ID or Name)
    (openingStocks || []).forEach(os => {
      let idStr = (os.item?._id || os.item)?.toString();
      if (!idStr) {
        const name = (os.name || '').trim().toLowerCase();
        idStr = nameToIdMap[name];
      }

      if (idStr) {
        if (!map[idStr]) map[idStr] = { qty: 0, amount: 0 };
        const q = Number(os.quantity || os.availableQty || os.stock || 0);
        const r = Number(os.purchaseRate || os.rate || os.unitPrice || os.price || 0);
        map[idStr].qty += q;
        map[idStr].amount += (q * r);
        map[idStr].hasOpenStock = true;
      }
    });

    return map;
  }, [purchases, openingStocks, nameToIdMap]);

  const activeItemIds = useMemo(() => {
    const ids = new Set();
    
    // 1. From Inventory records
    (inventory || []).forEach(inv => {
      const id = inv.item?._id || inv.item;
      if (id) ids.add(id.toString());
    });

    // 2. From Purchases (including all items in multi-item purchases)
    (purchases || []).forEach(p => {
      const items = (p.items && Array.isArray(p.items)) ? p.items : [p];
      items.forEach(it => {
        const id = it.item?._id || it.item;
        if (id) ids.add(id.toString());
      });
    });

    // 3. From Opening Stocks
    (openingStocks || []).forEach(os => {
      const id = os.item?._id || os.item;
      if (id) ids.add(id.toString());
    });

    return ids;
  }, [inventory, purchases, openingStocks]);

  const activeInventoryRaw = useMemo(() => inventory, [inventory]);

  const activeInventory = useMemo(() => {
    // 1. Start with Master Items as the base, but ONLY those with transactional activity
    const baseItems = masterItems
      .filter(mi => mi.active !== false)
      .filter(mi => activeItemIds.has(mi._id.toString())) // The "Gatekeeper" Filter
      .map(mi => ({
        _id: mi._id,
        name: mi.itemName || mi.name,
        itemName: mi.itemName || mi.name,
        itemCode: mi.itemCode,
        sku: mi.sku,
        category: typeof mi.category === 'object' ? (mi.category?.categoryName || mi.category?.title || mi.category?.name) : mi.category,
        categoryId: typeof mi.category === 'object' ? mi.category?._id?.toString() : mi.category,
        brand: typeof mi.brand === 'object' ? (mi.brand?.brandName || mi.brand?.title || mi.brand?.name) : mi.brand,
        unit: typeof mi.units === 'object' ? (mi.units?.unitName || mi.units?.title || mi.units?.name) : (mi.units || mi.unit || 'nos'),
        units: mi.units,
        minQty: Number(mi.minStock || mi.minQty || 0),
        unitPrice: Number(mi.purchaseAmount || mi.unitPrice || 0),
        isUnique: !!mi.isUnique,
        uniqueName: mi.uniqueName || "",
        hasBatch: !!mi.hasBatch,
        hsnCode: mi.hsnSacCode || mi.hsnCode || "",
        barcode: mi.barcode || "",
        description: mi.itemDescription || mi.description || "",
        itemType: mi.itemType,
        isMaster: true,
        createdAt: mi.createdAt,
        supplier: typeof mi.supplier === 'object' ? (mi.supplier.name || mi.supplier.title) : (mi.supplier || 'N/A'),
        supplierId: typeof mi.supplier === 'object' ? mi.supplier?._id?.toString() : mi.supplier
      }));
      
      
    const masterIds = new Set(baseItems.map(i => i._id.toString()));
    const masterNames = new Set(baseItems.map(i => (i.itemName || i.name || '').trim().toLowerCase()));

    // 2. Add any items from Inventory that are NOT in Master (Fallback for legacy)
    inventory.forEach(inv => {
      const masterId = inv.item?._id || inv.item;
      if (masterId && !masterIds.has(masterId.toString())) {
        const name = (inv.itemName || inv.name || '').trim();
        if (!masterNames.has(name.toLowerCase())) {
          baseItems.push({
            ...inv,
            name,
            itemName: name,
            isMaster: false
          });
          masterNames.add(name.toLowerCase());
        }
      }
    });

    return baseItems;
  }, [masterItems, inventory, activeTab]);

  const allAggregatedItems = useMemo(() => {
    const itemMap = {};
    
    // Group all inventory records by Item ID to show unique items in the Master Table
    inventory.forEach(inv => {
      const item = inv.item || {};
      // Use the Item ID as the grouping key
      const itemId = (item._id || inv.item)?.toString();
      if (!itemId) return;

      const isOpeningStock = inv.invoiceNo === 'OPEN-STOCK' || inv.supplier === 'Opening Stock';

      if (!itemMap[itemId]) {
        itemMap[itemId] = {
          _id: itemId,
          name: item.itemName || item.name || inv.name || 'Unknown',
          itemName: item.itemName || item.name || inv.name || 'Unknown',
          itemCode: item.itemCode || inv.itemCode || 'MASTER',
          sku: item.sku || inv.sku || '',
          category: typeof item.category === 'object' ? (item.category?.categoryName || item.category?.title || item.category?.name) : (item.category || inv.category || 'N/A'),
          categoryId: (item.category?._id || item.category || inv.category)?.toString(),
          brand: typeof item.brand === 'object' ? (item.brand?.brandName || item.brand?.title || item.brand?.name) : (item.brand || inv.brand || 'N/A'),
          unit: typeof item.units === 'object' ? (item.units?.unitName || item.units?.title || item.units?.name) : (item.units || item.unit || inv.unit || 'nos'),
          // Track min stock (use max if multiple records exist for safety)
          minStock: Number(inv.minStock || item.minStock || item.minQty || 0),
          minQty: Number(inv.minStock || item.minStock || item.minQty || 0),
          availableQty: 0,
          _stock: 0,
          totalPurchaseAmount: 0,
          isUnique: !!(item.isUnique || inv.isUnique),
          uniqueName: item.uniqueName || inv.uniqueName || "",
          hasBatch: !!(item.hasBatch || inv.hasBatch),
          createdAt: item.createdAt || inv.createdAt,
          // Pull vendor info from the most recent inventory record
          supplier: typeof inv.vendor === 'object' ? (inv.vendor.vendorName || inv.vendor.name) : (inv.vendor || 'N/A'),
          supplierId: (inv.vendor?._id || inv.vendor)?.toString(),
          item: item, // Pass full item object for child components
          hasPurchase: false,
          hasOpenStock: false
        };
      }

      // Set flags for tab redirection logic
      if (isOpeningStock) {
        itemMap[itemId].hasOpenStock = true;
      } else {
        itemMap[itemId].hasPurchase = true;
      }

      // Aggregate quantities and values directly from the Inventoried model
      const stock = Number(inv.stock || 0);
      itemMap[itemId].availableQty += stock;
      itemMap[itemId]._stock += stock;
      // Calculate current inventory value based on the purchase rate of each record
      itemMap[itemId].totalPurchaseAmount += (stock * Number(inv.purchaseRate || 0));
    });

    return Object.values(itemMap);
  }, [inventory, activeTab]);

  const isDateInRange = (dateInput) => {
    if (!dateInput) return true;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return true;
    
    // Normalize to start of day for comparison
    const dS = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

    if (filters.fromDate) {
      const f = new Date(filters.fromDate);
      if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) return false;
    }
    if (filters.toDate) {
      const t = new Date(filters.toDate);
      if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) return false;
    }

    if (filters.date && filters.date !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

      if (filters.date === 'today') return dS === today;
      
      if (filters.date === 'this_week') {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return dS >= start.getTime();
      }
      if (filters.date === 'this_month') {
        const start = new Date(today);
        start.setDate(start.getDate() - 30);
        return dS >= start.getTime();
      }
      if (filters.date === 'this_year') {
        const start = new Date(today);
        start.setDate(start.getDate() - 364);
        return dS >= start.getTime();
      }
    }
    return true;
  };

  const filtered = useMemo(() => {
    const term = search?.toLowerCase() || "";
    return allAggregatedItems.filter(i => {
      const name = (i.itemName || i.name || "").toLowerCase();
      const searchMatch = !term || (
        name.includes(term) ||
        (i.sku && i.sku.toLowerCase().includes(term)) ||
        (i.itemCode && i.itemCode.toLowerCase().includes(term))
      );
      
      // Flexible matching for Category (check ID and Title)
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const itemCatName = String(i.category || "").toLowerCase().trim();
      const itemCatId = String(i.categoryId || "").toLowerCase().trim();
      const catMatch = catValue === "all" || catValue === "all categories" || itemCatName === catValue || itemCatId === catValue || itemCatName.includes(catValue);
      
      const subCatValue = String(filters.subCategory || "all").toLowerCase().trim();
      const itemSubCatName = String(i.subCategory || i.subCategoryName || "").toLowerCase().trim();
      const itemSubCatId = String(i.subCategoryId || "").toLowerCase().trim();
      const subCatMatch = subCatValue === "all" || subCatValue === "all sub categories" || itemSubCatName === subCatValue || itemSubCatId === subCatValue || itemSubCatName.includes(subCatValue);

      // Flexible matching for Supplier/Vendor (check ID and Title)
      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const itemSupplierName = String(i.supplier || "").toLowerCase().trim();
      const itemSupplierId = String(i.supplierId || "").toLowerCase().trim();
      const supplierMatch = supplierValue === "all" || supplierValue === "all vendors" || itemSupplierName === supplierValue || itemSupplierId === supplierValue;
      
      const available = Number(i.availableQty) || 0;
      const min = Number(i.minQty || i.minStock || 0);

      let statusMatch = true;
      if (filters.status === "low") statusMatch = available <= min && available > 0;
      else if (filters.status === "out") statusMatch = available <= 0;
      else if (filters.status === "in") statusMatch = available > min;

      // Activity-aware Date Matching
      // Check if item creation OR any associated transaction falls in range
      const itemId = i._id?.toString();
      const itemStockRecords = (inventory || []).filter(inv => (inv.item?._id || inv.item)?.toString() === itemId);
      const activityDates = [i.createdAt, i.date, ...itemStockRecords.map(inv => inv.invoiceDate || inv.date || inv.createdAt)].filter(Boolean);
      
      let dateMatch = true;
      if (filters.fromDate || filters.toDate || (filters.date && filters.date !== 'all')) {
        // If any date filter is active, we require at least one activity date to match
        dateMatch = activityDates.length > 0 && activityDates.some(d => isDateInRange(d));
      }

      return searchMatch && catMatch && subCatMatch && supplierMatch && statusMatch && dateMatch;
    });
  }, [allAggregatedItems, search, filters]);

  const filteredPurchases = useMemo(() => {
    const term = search?.toLowerCase() || "";
    //updated Halimunnisa 2026-06-08
    //Enhanced the filtering logic for purchases to allow comprehensive search across multiple fields, including nested item details. This improvement enables users to easily find specific purchase records based on a wide range of criteria, such as vendor name, invoice number, category, subcategory, item name/code/sku, and more. The flexible search now also handles various data structures and edge cases gracefully, providing a more powerful and user-friendly experience when managing purchase records in the inventory system.
    
    const resolveName = (value, list) => {
      if (!value) return '';
      if (typeof value === 'object') return value.storeName || value.vendorName || value.title || value.name || value.label || value.categoryName || '';
      const idStr = String(value);
      if (/^[0-9a-fA-F]{24}$/.test(idStr) && Array.isArray(list)) {
        const match = list.find(item => String(item._id) === idStr);
        if (match) return match.name || match.title || match.storeName || match.vendorName || match.label || match.categoryName || match.subCategoryName || '';
      }
      return idStr;
    };

    return (purchases || []).filter(p => {
      // Directly read all flat fields from the purchase record (PurchaseMaster structure)
      const vendorResolved = resolveName(p.vendor || p.supplier, suppliers);
      const vendorName = String(p.vendorName || vendorResolved || "").toLowerCase().trim();
      const vendorId = typeof p.vendor === 'object' ? p.vendor?._id?.toString() : (p.vendor?.toString());
      const invNo = (p.invoiceNumber || p.invoiceNo || p.billNo || "").toLowerCase();
      //updated Halimunnisa 2026-06-08
      // Direct flat-field search across all visible table columns
      const categoryResolved = resolveName(p.category, cats);
      const category = String(p.categoryName || categoryResolved || "").toLowerCase();
      
      const subCategoryResolved = resolveName(p.subCategory, subCats);
      const subCategory = String(p.subCategoryName || subCategoryResolved || "").toLowerCase();
      
      const articleId = (p.articleId || "").toLowerCase();
      const description = (p.description || "").toLowerCase();
      const information = (p.information || "").toLowerCase();
      const internalType = (p.internalType || "").toLowerCase();
      const shutterType = (p.shutterType || "").toLowerCase();
      const remark = (p.remark || p.remarks || "").toLowerCase();
      const version = (p.version || "").toLowerCase();

      // Also search nested item fields (for non-PurchaseMaster records)
      const purchaseItems = (p.items && Array.isArray(p.items)) ? p.items : [];
      const itemMatch = purchaseItems.some(it => {
        const itemName = (it.itemName || it.item?.itemName || it.item?.name || "").toLowerCase();
        const itemCode = (it.itemCode || it.item?.itemCode || it.code || "").toLowerCase();
        const sku = (it.sku || it.item?.sku || "").toLowerCase();
        return itemName.includes(term) || itemCode.includes(term) || sku.includes(term);
      });

      const searchMatch = !term || 
        vendorName.includes(term) || 
        invNo.includes(term) ||
        category.includes(term) ||
        subCategory.includes(term) ||
        articleId.includes(term) ||
        description.includes(term) ||
        information.includes(term) ||
        internalType.includes(term) ||
        shutterType.includes(term) ||
        remark.includes(term) ||
        version.includes(term) ||
        itemMatch;
      
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const itemCatObj = p.item?.category || {};
      const catName = String(p.categoryName || (typeof p.category === 'object' ? (p.category?.title || p.category?.name || p.category?.categoryName) : p.category) || itemCatObj.title || itemCatObj.categoryName || itemCatObj.name || "").toLowerCase().trim();
      const catId = String(typeof p.category === 'object' ? (p.category?._id?.toString()) : (p.category || itemCatObj._id?.toString() || "")).toLowerCase().trim();
      const catMatch = catValue === "all" || catValue === "all categories" || catName === catValue || catId === catValue || catName.includes(catValue);
      
      const subCatValue = String(filters.subCategory || "all").toLowerCase().trim();
      const itemSubCatObj = p.item?.subCategory || {};
      const subCatName = String(p.subCategoryName || (typeof p.subCategory === 'object' ? (p.subCategory?.title || p.subCategory?.name || p.subCategory?.subCategoryName) : p.subCategory) || itemSubCatObj.title || itemSubCatObj.subCategoryName || itemSubCatObj.name || "").toLowerCase().trim();
      const subCatId = String(typeof p.subCategory === 'object' ? (p.subCategory?._id?.toString()) : (p.subCategory || itemSubCatObj._id?.toString() || "")).toLowerCase().trim();
      const subCatMatch = subCatValue === "all" || subCatValue === "all sub categories" || subCatName === subCatValue || subCatId === subCatValue;
      
      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const supplierMatch = supplierValue === "all" || supplierValue === "all vendors" || vendorName === supplierValue || vendorId?.toLowerCase().trim() === supplierValue;
      
      const dateMatch = isDateInRange(p.invoiceDate || p.date || p.createdAt);
      
      return searchMatch && catMatch && subCatMatch && supplierMatch && dateMatch;
    });
  }, [purchases, search, filters]);


  const filteredOpenStock = useMemo(() => {
    const term = search?.toLowerCase() || "";
    
    // Only show records from the dedicated Opening Stock table (as requested)
    const allOpenStockRaw = (openingStocks || []);

    return allOpenStockRaw.filter(os => {
      // Normalize name and code for search
      const name = String(os.name || os.item?.itemName || os.itemName || "").toLowerCase();
      const code = String(os.itemCode || os.item?.itemCode || os.code || "").toLowerCase();
      
      const searchMatch = !term || name.includes(term) || code.includes(term);
      
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const itemCatObj = os.item?.category || {};
      const catName = String(typeof os.category === 'object' ? (os.category?.title || os.category?.name) : (os.category || itemCatObj.title || itemCatObj.categoryName || itemCatObj.name || "")).toLowerCase().trim();
      const catId = String(typeof os.category === 'object' ? (os.category?._id?.toString()) : (os.category || itemCatObj._id?.toString() || "")).toLowerCase().trim();
      const catMatch = catValue === "all" || catValue === "all categories" || catName === catValue || catId === catValue || catName.includes(catValue);
      
      const subCatValue = String(filters.subCategory || "all").toLowerCase().trim();
      const itemSubCatObj = os.item?.subCategory || {};
      const subCatName = String(typeof os.subCategory === 'object' ? (os.subCategory?.title || os.subCategory?.name || os.subCategory?.subCategoryName) : (os.subCategory || itemSubCatObj.title || itemSubCatObj.subCategoryName || itemSubCatObj.name || "")).toLowerCase().trim();
      const subCatId = String(typeof os.subCategory === 'object' ? (os.subCategory?._id?.toString()) : (os.subCategory || itemSubCatObj._id?.toString() || "")).toLowerCase().trim();
      const subCatMatch = subCatValue === "all" || subCatValue === "all sub categories" || subCatName === subCatValue || subCatId === subCatValue;

      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const itemSuppObj = os.item?.supplier || os.item?.vendor || os.supplier || os.vendor || {};
      const itemSupplierName = String(typeof itemSuppObj === 'object' ? (itemSuppObj.vendorName || itemSuppObj.name || itemSuppObj.title) : (itemSuppObj || "")).toLowerCase().trim();
      const itemSupplierId = String(typeof itemSuppObj === 'object' ? itemSuppObj._id?.toString() : (itemSuppObj || "")).toLowerCase().trim();
      const supplierMatch = supplierValue === "all" || supplierValue === "all vendors" || itemSupplierName === supplierValue || itemSupplierId === supplierValue;

      const dateMatch = isDateInRange(os.createdAt || os.date || os.invoiceDate);
      
      return searchMatch && catMatch && subCatMatch && supplierMatch && dateMatch;
    }).map(os => ({
      ...os,
      name: os.name || os.item?.itemName || os.itemName || 'Unknown Item',
      itemCode: os.itemCode || os.item?.itemCode || os.code || '',
      category: os.category || os.item?.category || '',
      store: os.store || 'Main Store',
      invoiceDate: os.createdAt || os.date || os.invoiceDate,
      availableQty: os.availableQty || os.quantity || 0,
      unitPrice: os.rate || os.unitPrice || 0,
      rate: os.rate || os.unitPrice || 0
    }));
  }, [openingStocks, inventory, search, filters]);

  const filteredRequests = useMemo(() => {
    const term = search?.toLowerCase() || "";
    const currentUser = userObj || state?.user;
    const userRole = (currentUser?.role || "").toLowerCase();

    return materialRequests.filter(r => {
      const status = (r.status || "").toUpperCase();
      

//updated Halimunnisa 2026-06-08
// Flexible status matching (allow partial matches for better UX, e.g. "delivered" should match "partially_delivered")
// Status filter
const filterStatus = String(filters.status || "all").toLowerCase().trim();
const normalizedStatus = status.toLowerCase();
const statusMatch = filterStatus === "all" || normalizedStatus.includes(filterStatus) || filterStatus.includes(normalizedStatus);

      // Customer filter
      const filterCustomer = String(filters.customerName || "all").toLowerCase().trim();
      let customerMatch = filterCustomer === "all";
      if (!customerMatch) {
        const proj = projectsLocal.find(p => p._id === r.projectId || p.id === r.projectId);
        const customerName = (proj?.customerName || proj?.name || "").toLowerCase().trim();
        customerMatch = customerName === filterCustomer || customerName.includes(filterCustomer);
      }

      // Date filter
      const dateMatch = isDateInRange(r.createdAt || r.date || r.requestDate);

      let searchMatch = true;
      if (term) {
        const proj = projectsLocal.find(p => p._id === r.projectId || p.id === r.projectId);
        const customerName = (proj?.customerName || "").toLowerCase();
        searchMatch = customerName.includes(term) || (r._id || "").toLowerCase().includes(term) || (r.requestNo || "").toLowerCase().includes(term);
      }

      return searchMatch && statusMatch && customerMatch && dateMatch;
    });
  }, [materialRequests, projectsLocal, search, state.user, filters]);

  const consolidatedSales = useMemo(() => {
    const term = search?.toLowerCase() || "";
    
    return (sales || []).filter(s => {
      const type = s.type || (s.recordType === 'material_request' ? 'PROJECT' : 'DIRECT');
      const isProject = type === 'PROJECT';
      
      let customerName = "";
      let items = s.items || [];
      let invoiceNo = s.invoiceNo || "";
      let refNo = isProject ? (s.invoiceNo || s.requestNo || "") : s.invoiceNo;

      if (isProject) {
        const proj = s.project || s.originalRequest?.project || {};
        customerName = proj.customerName || s.customer || "Project Site";
      } else {
        customerName = typeof s.customer === 'object' ? (s.customer?.name || "") : (s.customer || "");
      }

      const itemSummary = items.map(it => {
        const name = it.itemName || it.item?.description || it.item?.itemName || it.item?.articleId || it.item?.name || "";
        const q = it.deliveredQty || it.qty || 0;
        return name ? `${name} (x${q})` : "";
      }).filter(Boolean).join(", ");

      const searchMatch = !term || 
        customerName.toLowerCase().includes(term) || 
        String(invoiceNo).toLowerCase().includes(term) || 
        String(refNo).toLowerCase().includes(term) ||
        itemSummary.toLowerCase().includes(term);
      
      const dateMatch = isDateInRange(s.date || s.createdAt);

      // Category and Supplier filters
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const category = s.category || (items[0]?.category?.title || items[0]?.category || 'N/A');
      const catMatch = catValue === "all" || String(category).toLowerCase().includes(catValue);

      const supplierValue = String(filters.supplier || "all").toLowerCase().trim();
      const supplierMatch = supplierValue === "all" || customerName.toLowerCase().includes(supplierValue);

      return searchMatch && dateMatch && catMatch && supplierMatch;
    }).map(s => {
      const type = s.type || (s.recordType === 'material_request' ? 'PROJECT' : 'DIRECT');
      const isProject = type === 'PROJECT';
      const items = s.items || [];
      
      let customerName = "";
      let projectId = "";
      if (isProject) {
        const proj = s.project || s.originalRequest?.project || {};
        customerName = proj.customerName || s.customer || "Project Site";
        projectId = proj.projectId || s.projectId || "";
      } else {
        customerName = typeof s.customer === 'object' ? (s.customer?.name || "") : (s.customer || "");
        projectId = s.invoiceNo || "";
      }

      const itemSummary = items.map(it => {
        const name = it.itemName || it.item?.description || it.item?.itemName || it.item?.articleId || it.item?.name || "";
        const q = it.deliveredQty || it.qty || 0;
        return name ? `${name} (x${q})` : "";
      }).filter(Boolean).join(", ");

      const calculatedNet = items.reduce((sum, it) => {
        const q = Number(it.deliveredQty || it.qty || 0);
        const p = Number(it.rate || it.unitPrice || it.item?.unitPrice || 0);
        return sum + (q * p);
      }, 0);

      return {
        ...s,
        recordType: isProject ? 'material_request' : 'sale',
        invoiceNo: isProject ? (s.invoiceNo || s.requestNo || "—") : (s.invoiceNo || "—"),
        customer: customerName,
        projectId: projectId,
        itemSummary,
        totalQty: items.reduce((sum, it) => sum + (Number(it.deliveredQty || it.qty) || 0), 0),
        netPayable: s.netPayable || s.totalAmount || calculatedNet,
        status: s.status || 'Paid',
        displayStatus: s.status
      };
    }).sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  }, [sales, search, filters]);

  const allFormattedReturns = useMemo(() => {
    return [
      ...(Array.isArray(materialReturns) ? materialReturns : []).map(r => {
        const items = r.items || [];
        // Map item names robustly
        const itemNames = items.map(it => it.itemName || it.inventoryId?.item?.itemName || it.inventoryId?.item?.name || 'Item');
        const itemSummary = itemNames.length > 2 
          ? `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more` 
          : itemNames.join(", ");

        // Calculate amount based on inventory purchaseRate
        const calculatedAmount = items.reduce((sum, it) => {
          const rate = it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || it.unitPrice || 0;
          return sum + (Number(it.quantity || it.qty || 0) * Number(rate));
        }, 0);

        return {
          ...r,
          returnType: r.returnType === 'DAMAGE' ? 'Damage' : (r.returnType === 'PURCHASE' ? 'Purchase' : (r.returnType === 'SALE' ? 'Sale' : 'Site')),
          rawType: (r.returnType || 'site').toLowerCase(),
          date: r.returnDate || r.date || r.createdAt,
          party: r.project?.customerName || r.projectDetails?.customerName || 'Site',
          partyId: (r.project?._id || r.projectDetails?.customerId || r.projectId || r.partyId)?.toString(),
          amount: Number(r.totalAmount || r.totalValue || calculatedAmount || 0),
          receivedAmount: Number(r.refundAmount || r.receivedAmount || 0),
          itemQuantity: items.reduce((sum, it) => sum + (Number(it.quantity || it.qty) || 0), 0),
          itemSummary: itemSummary || "No items",
          items: items.map(it => ({
            ...it,
            qty: Number(it.quantity || it.qty || 0),
            unit: it.inventoryId?.item?.units?.name || it.unit || "Units"
          })),
          reason: r.notes || "General Return",
          status: (r.status || 'pending').toLowerCase(),
          invoiceNo: r.originalInvoiceNo || r.invoiceNo || r.project?.projectId || r.projectId || "—",
          projectId: r.project?.projectId || r.projectId || "—",
          returnNo: r.returnNo || `SR-${r._id?.toString().slice(-6).toUpperCase()}`,
          returnId: r.returnNo || `SR-${r._id?.toString().slice(-6).toUpperCase()}`,
          category: typeof r.category === 'object' ? (r.category?.title || r.category?.name) : (r.category || (r.items?.[0]?.category?.title || r.items?.[0]?.category || r.items?.[0]?.inventoryId?.item?.category?.title || 'N/A')),
          categoryId: (r.category?._id || r.category || r.items?.[0]?.category?._id || r.items?.[0]?.category || r.items?.[0]?.inventoryId?.item?.category?._id || r.items?.[0]?.inventoryId?.item?.category)?.toString(),
          originalData: r
        };
      }),
      ...(Array.isArray(purchaseReturns) ? purchaseReturns : (purchaseReturns?.returns || purchaseReturns?.data || [])).map(r => {
        const items = r.items || [];
        const itemNames = items.map(it => it.itemName || it.item?.itemName || it.item?.name || "Item");
        const itemSummary = itemNames.length > 2 
          ? `${itemNames.slice(0, 2).join(", ")} + ${itemNames.length - 2} more` 
          : itemNames.join(", ");
        
        return {
          ...r,
          returnType: 'Purchase',
          rawType: 'purchase',
          date: r.date || r.returnDate || r.createdAt,
          party: typeof r.vendor === 'object' ? (r.vendor?.vendorName || r.vendor?.name) : (typeof r.supplier === 'object' ? r.supplier?.name : (r.vendor || r.supplier || 'Vendor')),
          partyId: (r.vendor?._id || r.vendor || r.supplier?._id || r.supplier || r.partyId)?.toString(),
          totalAmount: Number(r.totalAmount || 0),
          receivedAmount: Number(r.refundAmount || 0),
          amount: Number(r.totalAmount || 0),
          itemQuantity: items.reduce((sum, it) => sum + (Number(it.quantity || it.qty) || 0), 0),
          itemSummary: itemSummary || "No items",
          reason: r.reason || r.notes || "General Return",
          invoiceNo: r.invoiceNo || r.originalInvoiceNo || r.purchase?.invoiceNumber || r.purchase?.invoiceNo || "—",
          returnId: r.invoiceNo || r.originalInvoiceNo || r.purchase?.invoiceNumber || r.purchase?.invoiceNo || "—",
          store: typeof r.store === 'object' ? (r.store?.storeName || r.store?.name) : (r.store || 'Main Store'),
          items: items.map(it => ({
            ...it,
            name: it.itemName || it.item?.itemName || it.item?.name || "Unknown Item",
            category: it.item?.category || it.category
          })),
          status: (r.status || 'pending').toLowerCase(),
          category: typeof r.category === 'object' ? (r.category?.title || r.category?.name) : (r.category || (r.items?.[0]?.category?.title || r.items?.[0]?.category || r.items?.[0]?.item?.category?.title || 'N/A')),
          categoryId: (r.category?._id || r.category || r.items?.[0]?.category?._id || r.items?.[0]?.category || r.items?.[0]?.item?.category?._id || r.items?.[0]?.item?.category)?.toString(),
          originalData: r
        };
      })
    ];
  }, [materialReturns, purchaseReturns]);

  const rawSubTabReturns = useMemo(() => {
    const tab = String(returnsSubTab || '').toLowerCase();
    if (tab === 'master') return allFormattedReturns;
    if (tab === 'site') return allFormattedReturns.filter(r => r.rawType === 'site');
    if (tab === 'sales') return allFormattedReturns.filter(r => r.rawType === 'sale');
    if (tab === 'purchases') return allFormattedReturns.filter(r => r.rawType === 'purchase');
    if (tab === 'damaged') {
      return allFormattedReturns.filter(r => r.rawType === 'damage' || (r.items || []).some(item => item.damagedQty > 0));
    }
    return [];
  }, [allFormattedReturns, returnsSubTab]);

  const consolidatedReturns = useMemo(() => {
    return rawSubTabReturns.filter(r => {
      //updated Halimunnisa 2026-06-08
      const term = search?.toLowerCase() || "";
      const itemsMatch = (r.items || []).some(it => {
        const itemObj = it.item || it.inventoryId?.item || {};
        return [
          it.itemName, itemObj.name, itemObj.itemName,
          it.reason, it.notes,
          it.itemCode, itemObj.itemCode, it.sku, itemObj.sku,
          itemObj.vendorName,
          typeof itemObj.category === 'object' ? (itemObj.category?.title || itemObj.category?.name) : itemObj.category,
          itemObj.subCategory,
          itemObj.articleId,
          itemObj.description,
          itemObj.information,
          itemObj.internalType,
          itemObj.shutterType
        ].some(val => String(val || "").toLowerCase().includes(term));
      });

      const searchMatch = !term || itemsMatch || [
        r.party,
        r.returnId,
        r.invoiceNo,
        r.originalInvoiceNo,
        r.returnNo,
        r.returnType,
        r.rawType,
        r.reason,
        r.remarks,
        r.description,
        r.amount?.toString(),
        r.receivedAmount?.toString(),
        typeof r.category === 'object' ? (r.category?.title || r.category?.name) : r.category,
        typeof r.store === 'object' ? (r.store?.storeName || r.store?.name) : r.store,
        r.itemSummary,
        r.status
      ].some(val => String(val || "").toLowerCase().includes(term));
      
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const catName = String(r.category || "").toLowerCase().trim();
      const catId = String(r.categoryId || "").toLowerCase().trim();
      
      // Look up category name if catValue is an ID
      const selectedCat = cats.find(c => c._id === filters.category);
      const selectedCatName = selectedCat ? (selectedCat.categoryName || selectedCat.title || selectedCat.name || "").toLowerCase().trim() : "";
      
      const catMatch = catValue === "all" || 
                       catValue === "all categories" || 
                       catName === catValue || 
                       catId === catValue ||
                       (selectedCatName && catName === selectedCatName);
      
      const partyValue = String(filters.supplier || filters.party || "all").toLowerCase().trim();
      const itemPartyName = String(r.party || "").toLowerCase().trim();
      const itemPartyId = String(r.partyId || "").toLowerCase().trim();
      
      // Lookup party name if partyValue is an ID
      const selectedParty = [...suppliers, ...projectsLocal].find(p => p._id === (filters.supplier || filters.party));
      const selectedPartyName = selectedParty ? (selectedParty.vendorName || selectedParty.customerName || selectedParty.name || "").toLowerCase().trim() : "";

      const partyMatch = partyValue === "all" || 
                         partyValue === "all vendors" || 
                         itemPartyName === partyValue || 
                         itemPartyId === partyValue ||
                         (selectedPartyName && itemPartyName === selectedPartyName);
      
      const dateMatch = isDateInRange(r.date);
      return searchMatch && catMatch && partyMatch && dateMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [rawSubTabReturns, search, filters, cats, suppliers, projectsLocal]);
//updated Halimunnisa 2026-06-08
// The filteredDamagedItems logic has been enhanced to provide a more comprehensive and flexible search experience for damaged inventory records. The filtering now includes multiple fields such as invoice numbers, party names, item details, and damage reasons, allowing users to easily find specific damaged records based on a wide range of criteria. Additionally, the date filtering has been improved to ensure accurate matching of records within the specified date range. This enhancement significantly improves the usability and effectiveness of the damaged items management in the inventory system.
  const filteredDamagedItems = useMemo(() => {
    return damagedItems.filter(r => {
      const term = search?.toLowerCase().trim() || "";
      const invoiceStr = [
        r.invoice, r.invoiceNo, r.originalInvoiceNo, 
        r.returnRef?.invoiceNo, r.returnRef?.originalInvoiceNo, r.returnRef?.returnNo, 
        r.returnRef?.purchase?.invoiceNumber, r.returnRef?.purchase?.invoiceNo,
        r.returnRef?.project?.projectId, r.returnRef?.projectId,
        r.returnNo, r._id
      ].map(v => String(v || "").toLowerCase()).join(" ");
      
      let totalValue = r.totalDamagedValue || 0;
      if (totalValue === 0 && r.damagedItems) {
        totalValue = r.damagedItems.reduce((acc, curr) => acc + ((curr.damagedQty || 0) * (curr.rate || 0)), 0);
      }

      const partyStr = String(r.returnRef?.vendor?.name || r.returnRef?.vendor?.vendorName || r.returnRef?.supplier?.name || r.returnRef?.party || r.returnRef?.customer?.name || r.returnRef?.project?.customerName || "").toLowerCase().trim();

      const itemsMatch = (r.damagedItems || []).some(it => {
        const itemObj = it.item || {};
        return [
          it.itemName, itemObj.name, itemObj.itemName,
          it.reason,
          itemObj.itemCode,
          itemObj.vendorName,
          typeof itemObj.category === 'object' ? (itemObj.category?.title || itemObj.category?.name) : itemObj.category,
          itemObj.subCategory,
          itemObj.articleId,
          itemObj.description,
          itemObj.information,
          itemObj.internalType,
          itemObj.shutterType
        ].some(val => String(val || "").toLowerCase().includes(term));
      });

      const searchMatch = !term || 
        invoiceStr.includes(term) || 
        String(r.type || "").toLowerCase().includes(term) ||
        String(r.totalDamagedQty || "").toLowerCase().includes(term) ||
        String(totalValue || "").toLowerCase().includes(term) ||
        partyStr.includes(term) ||
        itemsMatch;

      const dateMatch = isDateInRange(r.date || r.createdAt || r.reportDate || r.returnRef?.createdAt || r.returnRef?.date);
      
      const catValue = String(filters.category || "all").toLowerCase().trim();
      const catMatch = catValue === "all" || catValue === "all categories" || (r.damagedItems || []).some(it => {
        const itemCatName = String(it.item?.category?.title || it.item?.category?.name || it.item?.category || "").toLowerCase().trim();
        const itemCatId = String(it.item?.category?._id || it.item?.category || "").toLowerCase().trim();
        return itemCatName === catValue || itemCatId === catValue || itemCatName.includes(catValue);
      });

      const partyValue = String(filters.supplier || filters.party || "all").toLowerCase().trim();
      const partyIdStr = String(r.returnRef?.vendor?._id || r.returnRef?.supplier?._id || r.returnRef?.partyId || r.returnRef?.customer?._id || r.returnRef?.projectId || "").toLowerCase().trim();
      const partyMatch = partyValue === "all" || partyValue === "all vendors" || partyStr === partyValue || partyIdStr === partyValue || partyStr.includes(partyValue);

      return searchMatch && dateMatch && catMatch && partyMatch;
    }).sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  }, [damagedItems, search, filters]);


  const lowStock = useMemo(() => 
    allAggregatedItems.filter(i => i.availableQty <= i.minQty && i.availableQty > 0), 
    [allAggregatedItems]
  );

  const noStock = useMemo(() => 
    allAggregatedItems.filter(i => i.availableQty <= 0), 
    [allAggregatedItems]
  );

  return {
    activeInventory,
    allAggregatedItems,
    filtered,
    filteredRequests,
    consolidatedSales,
    consolidatedReturns,
    filteredOpenStock,
    filteredPurchases,
    distributedMap,
    purchasedMap,
    lowStock,
    noStock,
    filteredDamagedItems
  };
};
